import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Tabs, Tab, Alert, Badge } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import { assetService } from '../../../services/assetService';
import ResponsiveModal from '../../../components/ResponsiveModal';
import WorkIdStatusProgress from './WorkIdStatusProgress';
import WorkIdImageAttachments from './WorkIdImageAttachments';
import GoogleGPSMap from '../../../components/GoogleGPSMap';
import type { Asset } from '../../../types/Asset';
import { getPriorityVariant, formatPriority } from '../utils/priorityDisplay';

interface WorkIdApproveModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdApproveModal: React.FC<WorkIdApproveModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [assetId, setAssetId] = useState<number | ''>(() =>
    workId?.asset?.id ? workId.asset.id : ''
  );
  const [comments, setComments] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<'approve' | 'attachments'>('approve');

  const assetRequired = !workId?.asset?.id;
  const isValid = assetRequired ? assetId && Number(assetId) > 0 : true;

  useEffect(() => {
    if (workId?.asset?.id) {
      setAssetId(workId.asset.id);
    } else {
      setAssetId('');
    }
    setComments(workId?.comments ?? '');
    setError(null);
    setActivePage('approve');
  }, [visible, workId]);

  useEffect(() => {
    const loadAssets = async () => {
      const serviceAreaId = workId?.foremanArea?.serviceArea?.id;
      if (!serviceAreaId) {
        setAssets([]);
        return;
      }
      setAssetsLoading(true);
      try {
        const data = await assetService.searchAssets({ serviceAreaId });
        setAssets(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error loading assets:', e);
        setAssets([]);
      } finally {
        setAssetsLoading(false);
      }
    };
    if (visible) loadAssets();
  }, [visible, workId?.foremanArea?.serviceArea?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId || !isValid) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload: {
        assetId?: number;
        comments?: string;
      } = {
        comments: comments.trim() || undefined,
      };
      if (assetId && Number(assetId) > 0) payload.assetId = Number(assetId);

      await workIdService.approve(workId.id, payload);
      onClose(); // Close modal first so it always closes even if onSuccess throws
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to approve work ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Work ID – ${workId.workIdentificationId ?? workId.id} (Approve)`;

  const sourceLabel = workId.createdFromForm?.id ? 'Form' : 'Express Work ID';
  const hasMapCoords =
    (workId.latitude != null && workId.longitude != null) ||
    (workId.asset?.startLatitude != null && workId.asset?.startLongitude != null);
  const mapLocation = hasMapCoords
    ? (workId.latitude != null && workId.longitude != null
        ? { lat: workId.latitude, lng: workId.longitude }
        : workId.asset?.startLatitude != null && workId.asset?.startLongitude != null
          ? { lat: workId.asset.startLatitude, lng: workId.asset.startLongitude }
          : undefined)
    : undefined;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="large"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Approving…' : 'Approve'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="tw-mb-3 tw-w-full">
          <strong className="tw-block tw-mb-1">Status</strong>
          <WorkIdStatusProgress status={workId.status} />
        </div>

        <Tabs
          activeKey={activePage}
          onSelect={(k) => k && setActivePage(k as 'approve' | 'attachments')}
          className="tw-mb-3"
        >
          <Tab eventKey="approve" title="Approve">
        <Row>
          <Col md={6} className="tw-mb-3 md:tw-mb-0">
            <div className="tw-mb-2">
              <strong className="tw-block tw-mb-1">Work ID details</strong>
          <Row>
            <Col md={6}>
              <p className="tw-mb-1">
                <strong>Reported by:</strong>{' '}
                {workId.reportedBy
                  ? `${workId.reportedBy.name} ${workId.reportedBy.lastName}`
                  : '—'}
              </p>
              <p className="tw-mb-1">
                <strong>Reported date:</strong>{' '}
                {workId.reportedOn
                  ? new Date(workId.reportedOn).toLocaleDateString()
                  : '—'}
              </p>
              <p className="tw-mb-0">
                <strong>Asset:</strong>{' '}
                {workId.asset
                  ? `${workId.asset.name} (${workId.asset.assetNumber})`
                  : '—'}
              </p>
            </Col>
            <Col md={6}>
              <p className="tw-mb-1">
                <strong>Foreman area:</strong>{' '}
                {workId.foremanArea?.name ?? '—'}
              </p>
              <p className="tw-mb-1">
                <strong>Priority:</strong>{' '}
                {workId.priority ? (
                  <Badge bg={getPriorityVariant(workId.priority)}>
                    {formatPriority(workId.priority)}
                  </Badge>
                ) : (
                  '—'
                )}
              </p>
              <p className="tw-mb-0">
                <strong>Source:</strong> {sourceLabel}
              </p>
            </Col>
          </Row>
        </div>

        <Form.Group className="tw-mb-2">
          <Form.Label>Comments</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments"
          />
        </Form.Group>

        <div className="tw-mb-2">
          <strong className="tw-block tw-mb-1">Required to approve</strong>
          {assetRequired && (
            <Form.Group className="tw-mb-2">
              <Form.Label>Asset (required)</Form.Label>
              {assetsLoading ? (
                <div className="tw-text-content-muted tw-text-sm">Loading assets…</div>
              ) : workId.foremanArea?.serviceArea?.id ? (
                <Form.Select
                  value={assetId}
                  onChange={(e) =>
                    setAssetId(e.target.value ? Number(e.target.value) : '')
                  }
                  required
                >
                  <option value="">Select an asset</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.assetNumber})
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <div className="tw-text-content-muted tw-text-sm">
                  No service area; cannot load assets.
                </div>
              )}
            </Form.Group>
          )}
          <p className="tw-text-sm tw-text-content-muted tw-mb-0">
            Add an asset if required, then click Approve.
          </p>
        </div>
          </Col>
          <Col md={6}>
            <strong className="tw-block tw-mb-1">Location</strong>
            {hasMapCoords && mapLocation ? (
              <div style={{ height: 260 }}>
                <GoogleGPSMap
                  startLocation={mapLocation}
                  endLocation={mapLocation}
                  onLocationSelect={() => {}}
                  locationType="start"
                  isPointActivity
                  readOnly
                  autoFitBounds={false}
                  hideInstructions
                  height={260}
                />
              </div>
            ) : (
              <div
                className="tw-rounded tw-border tw-border-line tw-flex tw-items-center tw-justify-center tw-text-content-muted"
                style={{ height: 200, backgroundColor: '#e9ecef' }}
              >
                No location data available
              </div>
            )}
          </Col>
        </Row>
          </Tab>
          <Tab eventKey="attachments" title={`Attachments (${workId.attachments?.length ?? 0})`}>
            <div className="tw-py-3">
              <strong className="tw-block tw-mb-2">Attachments</strong>
              <div className="tw-border tw-border-line tw-rounded tw-px-3 tw-py-3 tw-bg-surface-alt">
                <WorkIdImageAttachments
                  workIdId={workId.id}
                  attachments={workId.attachments ?? []}
                />
              </div>
            </div>
          </Tab>
        </Tabs>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdApproveModal;
